import { useState } from 'react';
import toast from 'react-hot-toast';
import { Circle, Pause, Play, Square } from 'lucide-react';
import { recordingApi } from '@/features/recordings/recording.api';
import { extractError } from '@/lib/axios';

/** Host-only recording control shown in the meeting top bar. */
export default function RecordControl({ meetingId }: { meetingId: string }): JSX.Element {
  const [recordingId, setRecordingId] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'recording' | 'paused'>('idle');
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(extractError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const start = () =>
    run(async () => {
      const rec = await recordingApi.start(meetingId);
      setRecordingId(rec.id);
      setStatus('recording');
      toast.success('Recording started');
    });
  const pause = () => run(async () => { await recordingApi.pause(recordingId!); setStatus('paused'); });
  const resume = () => run(async () => { await recordingApi.resume(recordingId!); setStatus('recording'); });
  const stop = () =>
    run(async () => {
      await recordingApi.stop(recordingId!);
      setStatus('idle');
      setRecordingId(null);
      toast.success('Recording saved');
    });

  if (status === 'idle') {
    return (
      <button onClick={start} disabled={busy} className="flex items-center gap-1 rounded-full bg-slate-800 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700">
        <Circle size={10} className="fill-red-500 text-red-500" /> Record
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {status === 'recording' ? (
        <button onClick={pause} disabled={busy} className="rounded-full bg-slate-800 p-1.5 text-slate-200" title="Pause">
          <Pause size={14} />
        </button>
      ) : (
        <button onClick={resume} disabled={busy} className="rounded-full bg-slate-800 p-1.5 text-slate-200" title="Resume">
          <Play size={14} />
        </button>
      )}
      <button onClick={stop} disabled={busy} className="rounded-full bg-red-600 p-1.5 text-white" title="Stop">
        <Square size={14} />
      </button>
    </div>
  );
}
